import React from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, LogOut, Package, ClipboardList, Settings as SettingsIcon, Image as ImageIcon, Bell } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = { name: "", description: "", price: "", stock: "", image: "", category: "", active: true };

export default function AdminDashboard() {
  const { admin, loading, logout, authHeader } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = React.useState("products");
  const [products, setProducts] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [shippingFee, setShippingFee] = React.useState("");
  const [siteContent, setSiteContent] = React.useState(null);
  const [savingSite, setSavingSite] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [waitlist, setWaitlist] = React.useState([]);
  const [editing, setEditing] = React.useState(null); // null | product object | "new"
  const [form, setForm] = React.useState(EMPTY);
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);

  React.useEffect(() => {
    if (!loading && !admin) navigate("/admin/login");
  }, [admin, loading, navigate]);

  const loadProducts = React.useCallback(async () => {
    const { data } = await axios.get(`${API}/products?include_inactive=true`);
    setProducts(data);
  }, []);

  const loadOrders = React.useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/orders`, { headers: authHeader() });
    setOrders(data);
  }, [authHeader]);

  const loadSettings = React.useCallback(async () => {
    const { data } = await axios.get(`${API}/settings`);
    setShippingFee(String(data.shipping_fee ?? 5));
  }, []);

  const loadSiteContent = React.useCallback(async () => {
    const { data } = await axios.get(`${API}/site-content`);
    setSiteContent(data);
  }, []);

  const loadWaitlist = React.useCallback(async () => {
    const { data } = await axios.get(`${API}/admin/waitlist`, { headers: authHeader() });
    setWaitlist(data);
  }, [authHeader]);

  React.useEffect(() => {
    if (admin) {
      loadProducts();
      loadOrders();
      loadSettings();
      loadSiteContent();
      loadWaitlist();
    }
  }, [admin, loadProducts, loadOrders, loadSettings, loadSiteContent, loadWaitlist]);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (p) => {
    setForm({
      name: p.name,
      description: p.description || "",
      price: String(p.price),
      stock: p.stock == null ? "" : String(p.stock),
      image: p.image || "",
      category: p.category || "",
      active: p.active,
    });
    setEditing(p);
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg(null);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setMsg("Image upload failed — please try a smaller photo");
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMsg(null);
    const payload = {
      name: form.name,
      description: form.description,
      price: parseFloat(form.price) || 0,
      stock: form.stock === "" ? null : parseInt(form.stock, 10),
      image: form.image,
      category: form.category,
      active: form.active,
    };
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/products`, payload, { headers: authHeader() });
      } else {
        await axios.put(`${API}/admin/products/${editing.id}`, payload, { headers: authHeader() });
      }
      setEditing(null);
      await loadProducts();
    } catch (err) {
      setMsg(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await axios.delete(`${API}/admin/products/${p.id}`, { headers: authHeader() });
    await loadProducts();
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    await axios.put(
      `${API}/admin/settings`,
      { shipping_fee: parseFloat(shippingFee) || 0 },
      { headers: authHeader() },
    );
    setMsg("Shipping fee saved");
    setTimeout(() => setMsg(null), 2500);
  };

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await axios.post(`${API}/admin/upload`, fd, {
      headers: { ...authHeader() },
    });
    return process.env.REACT_APP_BACKEND_URL + data.url;
  };

  const handleSiteImage = (field) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setSiteContent((c) => ({ ...c, [field]: url }));
    } catch (err) {
      setMsg("Image upload failed — please try a smaller photo");
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryImage = (idx) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setSiteContent((c) => {
        const gallery = [...(c.gallery || [])];
        gallery[idx] = { ...gallery[idx], imageUrl: url };
        return { ...c, gallery };
      });
    } catch (err) {
      setMsg("Image upload failed — please try a smaller photo");
    } finally {
      setUploading(false);
    }
  };

  const updateGalleryField = (idx, field, value) => {
    setSiteContent((c) => {
      const gallery = [...(c.gallery || [])];
      gallery[idx] = { ...gallery[idx], [field]: value };
      return { ...c, gallery };
    });
  };

  const addGalleryItem = () => {
    setSiteContent((c) => ({
      ...c,
      gallery: [
        ...(c.gallery || []),
        { id: `gal-${Date.now()}`, imageUrl: "", label: "", service: "" },
      ],
    }));
  };

  const removeGalleryItem = (idx) => {
    setSiteContent((c) => ({
      ...c,
      gallery: (c.gallery || []).filter((_, i) => i !== idx),
    }));
  };

  const saveSiteContent = async () => {
    if (!siteContent) return;
    setSavingSite(true);
    try {
      const { data } = await axios.put(
        `${API}/admin/site-content`,
        {
          hero_image: siteContent.hero_image,
          about_image: siteContent.about_image,
          bookings_image: siteContent.bookings_image,
          gallery: (siteContent.gallery || []).filter((g) => g.imageUrl),
        },
        { headers: authHeader() },
      );
      setSiteContent(data);
      setMsg("Website photos saved — refresh your site to see them");
      setTimeout(() => setMsg(null), 3500);
    } catch (err) {
      setMsg(err.response?.data?.detail || "Save failed");
    } finally {
      setSavingSite(false);
    }
  };

  if (loading || !admin) {
    return <div className="p-10 text-sm text-neutral-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-[#f7f4f1]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-10 border-b border-neutral-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em] text-neutral-500">
              Lash Dollz Vk Beauty
            </p>
            <h1 className="text-lg font-medium text-neutral-900">Admin dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate("/")}
              className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-900"
            >
              View site
            </button>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-neutral-700 hover:border-neutral-900"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="mb-6 flex gap-2">
          {[
            { key: "products", label: "Products", icon: Package },
            { key: "media", label: "Website Photos", icon: ImageIcon },
            { key: "orders", label: "Orders", icon: ClipboardList },
            { key: "waitlist", label: "Waitlist", icon: Bell },
            { key: "settings", label: "Settings", icon: SettingsIcon },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-[11px] font-medium uppercase tracking-[0.2em] transition-colors ${
                tab === t.key ? "bg-black text-white" : "bg-white text-neutral-600 hover:text-neutral-900"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {msg && (
          <p className="mb-4 rounded-lg bg-neutral-900 px-4 py-2 text-xs text-white" data-testid="admin-msg">
            {msg}
          </p>
        )}

        {tab === "products" && (
          <div>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
                Products ({products.length})
              </h2>
              <button
                type="button"
                onClick={openNew}
                data-testid="add-product-btn"
                className="inline-flex items-center gap-2 rounded-full bg-black px-5 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white hover:bg-neutral-800"
              >
                <Plus className="h-3.5 w-3.5" /> Add product
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((p) => (
                <article key={p.id} className="overflow-hidden rounded-2xl border border-neutral-200 bg-white" data-testid={`product-card-${p.id}`}>
                  {p.image ? (
                    <img src={p.image} alt={p.name} className="h-40 w-full object-cover" />
                  ) : (
                    <div className="flex h-40 w-full items-center justify-center bg-neutral-100 text-xs text-neutral-400">
                      No image
                    </div>
                  )}
                  <div className="p-4 text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-medium text-neutral-900">{p.name}</h3>
                      {!p.active && (
                        <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-[10px] uppercase text-neutral-600">
                          Hidden
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-neutral-900">€{Number(p.price).toFixed(2)}</p>
                    <p className="mt-0.5 text-xs text-neutral-500">
                      {p.stock == null ? "Stock not tracked" : `${p.stock} in stock`}
                    </p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        data-testid={`edit-product-${p.id}`}
                        className="inline-flex flex-1 items-center justify-center gap-1 rounded-full border border-neutral-300 py-1.5 text-[11px] uppercase tracking-[0.15em] text-neutral-700 hover:border-neutral-900"
                      >
                        <Pencil className="h-3 w-3" /> Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(p)}
                        data-testid={`delete-product-${p.id}`}
                        className="inline-flex items-center justify-center rounded-full border border-neutral-300 px-3 py-1.5 text-neutral-500 hover:border-red-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
              {products.length === 0 && (
                <p className="text-sm text-neutral-500">No products yet. Add your first product.</p>
              )}
            </div>
          </div>
        )}

        {tab === "media" && siteContent && (
          <div data-testid="media-panel">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
                Website photos {uploading && <span className="ml-2 normal-case tracking-normal text-neutral-400">· uploading…</span>}
              </h2>
              <button
                type="button"
                onClick={saveSiteContent}
                disabled={savingSite}
                data-testid="save-site-content-btn"
                className="inline-flex items-center gap-2 rounded-full bg-black px-5 py-2 text-[11px] font-medium uppercase tracking-[0.2em] text-white hover:bg-neutral-800 disabled:bg-neutral-300"
              >
                {savingSite ? "Saving…" : "Save all photos"}
              </button>
            </div>
            <p className="mb-6 text-xs text-neutral-500">
              Update the main photos on your site. After saving, refresh your live
              site (or Republish) to see the changes.
            </p>

            <div className="grid gap-5 sm:grid-cols-3">
              {[
                { field: "hero_image", label: "Homepage hero" },
                { field: "about_image", label: "About page photo" },
                { field: "bookings_image", label: "Bookings page photo" },
              ].map((slot) => (
                <div key={slot.field} className="rounded-2xl border border-neutral-200 bg-white p-4">
                  <p className="mb-2 text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
                    {slot.label}
                  </p>
                  {siteContent[slot.field] ? (
                    <img
                      src={siteContent[slot.field]}
                      alt={slot.label}
                      className="mb-3 h-40 w-full rounded-xl object-cover"
                    />
                  ) : (
                    <div className="mb-3 flex h-40 w-full items-center justify-center rounded-xl bg-neutral-100 text-xs text-neutral-400">
                      No image
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleSiteImage(slot.field)}
                    data-testid={`upload-${slot.field}`}
                    className="text-xs"
                  />
                </div>
              ))}
            </div>

            <div className="mt-8 mb-3 flex items-center justify-between">
              <h3 className="text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
                Gallery ({(siteContent.gallery || []).length})
              </h3>
              <button
                type="button"
                onClick={addGalleryItem}
                data-testid="add-gallery-item"
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-neutral-700 hover:border-neutral-900"
              >
                <Plus className="h-3.5 w-3.5" /> Add photo
              </button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {(siteContent.gallery || []).map((item, idx) => (
                <div key={item.id || idx} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`gallery-item-${idx}`}>
                  {item.imageUrl ? (
                    <img src={item.imageUrl} alt={item.label} className="mb-3 h-36 w-full rounded-xl object-cover" />
                  ) : (
                    <div className="mb-3 flex h-36 w-full items-center justify-center rounded-xl bg-neutral-100 text-xs text-neutral-400">
                      No image
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleGalleryImage(idx)}
                    className="mb-2 text-xs"
                  />
                  <input
                    type="text"
                    placeholder="Label (e.g. Wispy volume)"
                    value={item.label || ""}
                    onChange={(e) => updateGalleryField(idx, "label", e.target.value)}
                    className="mb-2 h-9 w-full rounded-full border border-neutral-300 px-3 text-xs outline-none focus:border-neutral-900"
                  />
                  <input
                    type="text"
                    placeholder="Category (e.g. Lash extensions)"
                    value={item.service || ""}
                    onChange={(e) => updateGalleryField(idx, "service", e.target.value)}
                    className="mb-2 h-9 w-full rounded-full border border-neutral-300 px-3 text-xs outline-none focus:border-neutral-900"
                  />
                  <button
                    type="button"
                    onClick={() => removeGalleryItem(idx)}
                    data-testid={`remove-gallery-${idx}`}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-neutral-500 hover:text-red-500"
                  >
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "waitlist" && (
          <div data-testid="waitlist-panel">
            <h2 className="mb-4 text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
              Back-in-stock waitlist
            </h2>
            {waitlist.length === 0 ? (
              <p className="text-sm text-neutral-500">No one is waiting on a sold-out product right now.</p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {waitlist.map((w) => (
                  <div key={w.product_id} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`waitlist-${w.product_id}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-neutral-900">{w.product_name}</h3>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                        {w.count} waiting
                      </span>
                    </div>
                    <ul className="mt-2 space-y-1 text-xs text-neutral-500">
                      {w.emails.map((e) => (
                        <li key={e} className="truncate">{e}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-4 text-xs text-neutral-400">
              When you restock a product (set its stock above 0), everyone here is emailed automatically and the list clears.
            </p>
          </div>
        )}

        {tab === "orders" && (
          <div data-testid="orders-panel">
            <h2 className="mb-4 text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
              Orders ({orders.length})
            </h2>
            <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
              <table className="w-full text-left text-sm">
                <thead className="bg-neutral-50 text-[11px] uppercase tracking-[0.15em] text-neutral-500">
                  <tr>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Items</th>
                    <th className="px-4 py-3">Total</th>
                    <th className="px-4 py-3">Method</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-neutral-100 align-top">
                      <td className="px-4 py-3">
                        <p className="font-medium text-neutral-900">{o.customer?.name}</p>
                        <p className="text-xs text-neutral-500">{o.customer?.email}</p>
                        {o.shipping_method === "delivery" && o.customer?.address && (
                          <p className="mt-1 text-xs text-neutral-500">
                            {o.customer.address}, {o.customer.city}, {o.customer.county} {o.customer.eircode}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-600">
                        {o.items?.map((i) => (
                          <div key={i.product_id}>{i.name} × {i.quantity}</div>
                        ))}
                      </td>
                      <td className="px-4 py-3">€{Number(o.total).toFixed(2)}</td>
                      <td className="px-4 py-3 text-xs capitalize">{o.shipping_method}</td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${
                            o.payment_status === "paid"
                              ? "bg-emerald-100 text-emerald-700"
                              : o.payment_status === "failed"
                              ? "bg-red-100 text-red-700"
                              : "bg-amber-100 text-amber-700"
                          }`}
                        >
                          {o.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-xs text-neutral-500">
                        {o.created_at ? new Date(o.created_at).toLocaleDateString() : ""}
                      </td>
                    </tr>
                  ))}
                  {orders.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-sm text-neutral-500">
                        No orders yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "settings" && (
          <form onSubmit={saveSettings} className="max-w-sm rounded-2xl border border-neutral-200 bg-white p-6" data-testid="settings-panel">
            <h2 className="text-sm font-medium uppercase tracking-[0.25em] text-neutral-700">
              Shipping
            </h2>
            <label className="mt-4 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
              Delivery fee (€) — Ireland
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={shippingFee}
              onChange={(e) => setShippingFee(e.target.value)}
              data-testid="shipping-fee-input"
              className="mt-1 h-10 w-full rounded-full border border-neutral-300 px-4 text-sm outline-none focus:border-neutral-900"
            />
            <p className="mt-2 text-xs text-neutral-500">Collection is always free for customers.</p>
            <button
              type="submit"
              data-testid="save-settings-btn"
              className="mt-4 h-10 w-full rounded-full bg-black text-[11px] font-medium uppercase tracking-[0.25em] text-white hover:bg-neutral-800"
            >
              Save
            </button>
          </form>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="product-modal">
          <form
            onSubmit={save}
            className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6"
          >
            <h2 className="text-lg font-medium text-neutral-900">
              {editing === "new" ? "Add product" : "Edit product"}
            </h2>
            <div className="mt-4 space-y-4 text-sm">
              <Input label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="form-description"
                  className="min-h-[80px] w-full rounded-2xl border border-neutral-300 px-4 py-2 outline-none focus:border-neutral-900"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Price (€)" type="number" required value={form.price} onChange={(v) => setForm({ ...form, price: v })} testid="form-price" />
                <Input label="Stock (blank = untracked)" type="number" value={form.stock} onChange={(v) => setForm({ ...form, stock: v })} testid="form-stock" />
              </div>
              <Input label="Category" value={form.category} onChange={(v) => setForm({ ...form, category: v })} testid="form-category" />
              <div>
                <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
                  Photo
                </label>
                {form.image && (
                  <img src={form.image} alt="preview" className="mb-2 h-32 w-full rounded-xl object-cover" />
                )}
                <input type="file" accept="image/*" onChange={handleImage} data-testid="form-image" className="text-xs" />
                {uploading && <p className="mt-1 text-xs text-neutral-500" data-testid="upload-status">Uploading photo…</p>}
              </div>
              <label className="flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="form-active"
                />
                Visible in shop
              </label>
              {msg && <p className="text-xs text-red-600">{msg}</p>}
            </div>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => { setEditing(null); setMsg(null); }}
                className="flex-1 rounded-full border border-neutral-300 py-2.5 text-[11px] uppercase tracking-[0.2em] text-neutral-700 hover:border-neutral-900"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                data-testid="save-product-btn"
                className="flex-1 rounded-full bg-black py-2.5 text-[11px] uppercase tracking-[0.2em] text-white hover:bg-neutral-800 disabled:bg-neutral-300"
              >
                {saving ? "Saving…" : "Save product"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Input({ label, type = "text", required, value, onChange, testid }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium uppercase tracking-[0.2em] text-neutral-700">
        {label} {required && <span className="text-neutral-400">*</span>}
      </label>
      <input
        type={type}
        step={type === "number" ? "0.01" : undefined}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="h-10 w-full rounded-full border border-neutral-300 px-4 text-sm outline-none focus:border-neutral-900"
      />
    </div>
  );
}
